import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MessageSquare } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { BrandWordmark } from "@/components/site/BrandCard";
import { BRANDS } from "@/config/site";
import { usePageMeta } from "@/hooks/usePageMeta";

const BrandRow = ({ brand, index }) => {
  const flip = index % 2 === 1;
  const accent = brand.tone === "pink" ? "var(--rose)" : "var(--sky)";
  return (
    <article id={brand.id} className="scroll-mt-28 border-t hairline py-16 md:py-24" data-testid={`brand-section-${brand.id}`}>
      <div className="container-x grid gap-10 lg:grid-cols-12 lg:items-center lg:gap-16">
        <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }}>
            <motion.div
              className={`relative aspect-[5/4] overflow-hidden ${flip ? "rounded-tl-[160px]" : "rounded-tr-[160px]"}`}
              variants={{ hidden: { clipPath: "inset(0 0 100% 0)" }, show: { clipPath: "inset(0 0 0% 0)", transition: { duration: 1.2, ease: [0.76, 0, 0.24, 1] } } }}
            >
              <img src={brand.image} alt={brand.imageAlt} loading="lazy" width="900" height="720" className="h-full w-full object-cover" />
            </motion.div>
          </motion.div>
        </div>
        <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="font-mono-label text-xs text-[var(--ink-2)]">{String(index + 1).padStart(2, "0")} / 08</span>
              <span className="h-px w-12" style={{ background: accent }} />
              <span className="eyebrow !text-[0.68rem]">{brand.category}</span>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 flex h-28 items-center text-5xl leading-none tracking-tight md:h-36 md:text-6xl"><BrandWordmark brand={brand} /></h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-[var(--ink-2)]">{brand.description}</p>
          </Reveal>
          <Reveal delay={0.24} className="mt-10 flex flex-wrap items-center gap-4">
            {brand.url ? (
              <a href={brand.url} target="_blank" rel="noopener noreferrer" className="btn-ink" data-testid={`brand-visit-${brand.id}`}>
                Visit Website <ArrowUpRight size={18} />
              </a>
            ) : (
              <span className="inline-flex items-center rounded-full border border-dashed border-ink/30 px-5 py-3 text-sm text-[var(--ink-2)]" data-testid={`brand-visit-${brand.id}`}>
                Website link coming soon
              </span>
            )}
            <Link to={`/partner-with-us?brand=${encodeURIComponent(brand.name)}`} className="btn-line" data-testid={`brand-enquiry-${brand.id}`}>
              Business Enquiry <MessageSquare size={16} />
            </Link>
          </Reveal>
        </div>
      </div>
    </article>
  );
};

export default function Brands() {
  usePageMeta("Our Brands", "Explore the Suvirika Retail brand portfolio: 99 Distributor, Grofkart, Avikka Cosmetics, Nanshya, Suvikka, Zeya, Krystal and Suvika Cosmetics.");
  return (
    <>
      <PageHero
        eyebrow="Our Businesses · Brand Portfolio"
        lines={["Eight brands,", <em key="i" className="italic font-light text-rose-ink">one parent.</em>]}
        intro="Each business in our portfolio has its own audience, identity and model. Browse the brands below, visit their websites where available, or reach out with a business enquiry."
        testId="brands-hero"
      >
        <div className="container-x mt-14">
          <nav aria-label="Brand index" className="flex flex-wrap gap-2" data-testid="brand-index">
            {BRANDS.map((b) => (
              <a key={b.id} href={`#${b.id}`} className="rounded-full border hairline px-4 py-2 text-sm transition-colors hover:border-[var(--ink)] hover:bg-white" data-testid={`brand-index-${b.id}`}>
                {b.name}
              </a>
            ))}
          </nav>
        </div>
      </PageHero>
      <div className="pb-12">
        {BRANDS.map((b, i) => <BrandRow key={b.id} brand={b} index={i} />)}
      </div>
    </>
  );
}
